import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useToast } from '../../contexts/ToastContext';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { FormGroup, FormLabel, FormInput } from '../../components/ui/form';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const navigate = useNavigate();
  const addToast = useToast();
  const { signInAdmin } = useAdminAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signInAdmin(email, password);
      addToast('Admin login successful!', 'success');
      navigate('/');
    } catch (error) {
      console.error('Admin login error:', error);
      
      // Check if this is an access denied error (unauthorized user)
      if (error.message && error.message.includes('Administrator privileges required')) {
        setAccessDenied(true);
        setError('UNAUTHORIZED ACCESS ATTEMPT DETECTED: This system is restricted to authorized administrators only. All access attempts are logged and monitored. Continued unauthorized access attempts may result in account suspension and enforcement action.');
      } else if (error.message && error.message.includes('Invalid login credentials')) {
        setError('Invalid administrator credentials. Please verify your email and password. Access attempts are monitored for security.');
      } else {
        setError(error.message || 'Authentication failed. Please contact your system administrator if you believe this is an error.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-start justify-center pt-20 py-12 px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
      accessDenied 
        ? 'bg-red-600 dark:bg-red-700' 
        : 'bg-gray-50 dark:bg-gray-900'
    }`}>
      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="animate-none">
          <Card className="animate-none bg-white dark:bg-gray-800 shadow-lg">
            <CardHeader>
            <h1 className="text-center text-2xl font-bold text-gray-900 dark:text-gray-100">
              Sign In
            </h1>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Please enter your credentials to continue.
            </p>
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
              <p className="text-xs text-yellow-800 dark:text-yellow-200 text-center">
                <span className="font-semibold">⚠️ RESTRICTED ACCESS:</span> All login attempts are monitored and logged.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <FormGroup>
                <FormLabel htmlFor="email">Email address</FormLabel>
                <FormInput
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="password">Password</FormLabel>
                <FormInput
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormGroup>
              
              {error && (
                <div className={`rounded-md p-4 ${
                  error.includes('UNAUTHORIZED ACCESS ATTEMPT') 
                    ? 'bg-red-600 dark:bg-red-700 border-2 border-red-800' 
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex">
                    {error.includes('UNAUTHORIZED ACCESS ATTEMPT') && (
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-100" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <div className={error.includes('UNAUTHORIZED ACCESS ATTEMPT') ? 'ml-3' : ''}>
                      <p className={`text-sm font-semibold ${
                        error.includes('UNAUTHORIZED ACCESS ATTEMPT') 
                          ? 'text-red-100 font-bold tracking-wide' 
                          : 'text-red-700 dark:text-red-400'
                      }`}>
                        {error.includes('UNAUTHORIZED ACCESS ATTEMPT') && 'ACCESS DENIED: '}
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
              
              <div className="flex items-center justify-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}

export default Login;